/**
 * EzLightbox - A simple lightbox component for EzShop
 */

interface EzLightboxOptions {
  group?: string;
  caption?: string;
  nextBtn?: string; // Custom label or HTML for next button
  prevBtn?: string; // Custom label or HTML for previous button
  closeBtn?: string; // Custom label or HTML for close button
}

class EzLightbox {
  private static instance: EzLightbox;
  private overlay: HTMLDialogElement | null = null;
  private images: HTMLAnchorElement[] = [];
  private currentIndex: number = 0;
  private currentOptions: EzLightboxOptions = {};

  private constructor() {
    this.init();
  }

  public static getInstance(): EzLightbox {
    if (!EzLightbox.instance) {
      EzLightbox.instance = new EzLightbox();
    }
    return EzLightbox.instance;
  }

  private init() {
    document.querySelectorAll<HTMLAnchorElement>('a[data-ezlightbox]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(anchor);
      });
    });
  }

  private open(anchor: HTMLAnchorElement) {
    // Parse options from data-ezlightbox
    const options: EzLightboxOptions = anchor.dataset.ezlightbox ? JSON.parse(anchor.dataset.ezlightbox) : {};
    this.currentOptions = options;
    if (options.group) {
      // Gallery mode: collect all anchors with same group
      this.images = Array.from(document.querySelectorAll<HTMLAnchorElement>(`a[data-ezlightbox*='"group":"${options.group}"']`));
      this.currentIndex = this.images.indexOf(anchor);
    } else {
      // Single image mode
      this.images = [anchor];
      this.currentIndex = 0;
    }
    this.showOverlay();
    this.updateContent(this.images[this.currentIndex].href, options.caption || anchor.title || anchor.querySelector('img')?.alt || '');
    if (this.images.length > 1) {
      this.addGalleryControls();
    }
  }

  private addGalleryControls() {
    if (!this.overlay) return;
    // Remove existing gallery buttons if any
    this.overlay.querySelector('.ezlightbox-prev')?.remove();
    this.overlay.querySelector('.ezlightbox-next')?.remove();
    // Add next/prev buttons outside content
    const prevBtn = document.createElement('button');
    prevBtn.className = 'ezlightbox-prev';
    prevBtn.innerHTML = this.currentOptions.prevBtn || '«';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.showPrev();
    });
    const nextBtn = document.createElement('button');
    nextBtn.className = 'ezlightbox-next';
    nextBtn.innerHTML = this.currentOptions.nextBtn || '»';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.showNext();
    });
    this.overlay.appendChild(prevBtn);
    this.overlay.appendChild(nextBtn);
  }

  private showPrev() {
    if (this.images.length < 2) return;
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    const anchor = this.images[this.currentIndex];
    const options: EzLightboxOptions = anchor.dataset.ezlightbox ? JSON.parse(anchor.dataset.ezlightbox) : {};
    this.currentOptions = options;
    this.updateContent(anchor.href, options.caption || anchor.title || anchor.querySelector('img')?.alt || '');
  }

  private showNext() {
    if (this.images.length < 2) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    const anchor = this.images[this.currentIndex];
    const options: EzLightboxOptions = anchor.dataset.ezlightbox ? JSON.parse(anchor.dataset.ezlightbox) : {};
    this.currentOptions = options;
    this.updateContent(anchor.href, options.caption || anchor.title || anchor.querySelector('img')?.alt || '');
  }

  private showOverlay() {
    // Only create overlay if it doesn't exist
    if (this.overlay) {
      if (typeof this.overlay.showModal === 'function') {
        this.overlay.showModal();
      } else {
        this.overlay.setAttribute('open', '');
      }
      return;
    }
    this.overlay = document.createElement('dialog');
    this.overlay.className = 'modal ezlightbox-modal';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('tabindex', '-1');
    // Overlay structure: close button, content (image + caption)
    this.overlay.innerHTML = `
      <button class="ezlightbox-close" aria-label="Close">${this.currentOptions.closeBtn || '×'}</button>
      <div class="modal-box ezlightbox-content" tabindex="0">
        <img src="" alt="" class="ezlightbox-img" />
        <div class="ezlightbox-caption"></div>
      </div>
    `;
    document.body.appendChild(this.overlay);
    // Show modal
    if (typeof this.overlay.showModal === 'function') {
      this.overlay.showModal();
    } else {
      this.overlay.setAttribute('open', '');
    }
    // Focus overlay for accessibility
    this.overlay.focus();
    // Close button
    this.overlay.querySelector('.ezlightbox-close')?.addEventListener('click', () => this.close());
    // Click outside to close (native dialog backdrop)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });
    // Escape key to close
    document.addEventListener('keydown', this.handleKeyDown);
  }

  private updateContent(src: string, caption: string) {
    if (!this.overlay) return;
    const img = this.overlay.querySelector<HTMLImageElement>('.ezlightbox-img');
    const captionDiv = this.overlay.querySelector<HTMLDivElement>('.ezlightbox-caption');
    if (img) {
      img.classList.remove('ezlightbox-fade');
      void img.offsetWidth; // trigger reflow for animation
      img.src = src;
      img.alt = caption;
      img.classList.add('ezlightbox-fade');
    }

    if (captionDiv) {
      captionDiv.textContent = caption;
    }
  }

  private close = () => {
    if (this.overlay) {
      if (typeof this.overlay.close === 'function') {
        this.overlay.close();
      }
      this.overlay.remove();
      this.overlay = null;
      document.removeEventListener('keydown', this.handleKeyDown);
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.close();
    } else if (e.key === 'ArrowLeft' && this.images.length > 1) {
      this.showPrev();
    } else if (e.key === 'ArrowRight' && this.images.length > 1) {
      this.showNext();
    }
  };
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => EzLightbox.getInstance());
} else {
  EzLightbox.getInstance();
}
